import { DayPreference } from '@/types/codegen-federation'
import { TIME_WINDOWS, DAYS_OF_WEEK } from './constants'
import { DayPreferenceInput, TheaterPreferencesVenue } from './types'

export const processDayPreferences = (dayPreferences: DayPreference[]) => {
  const dayTimeMap: Record<string, string[]> = {}
  dayPreferences.forEach((dayPref) => {
    const timeWindowIds = dayPref.timeRanges
      ?.map((range) => {
        const matchingWindow = TIME_WINDOWS.find((w) => w.startTime === range?.start && w.endTime === range?.end)
        return matchingWindow?.id || ''
      })
      .filter(Boolean)

    if (timeWindowIds?.length) {
      dayTimeMap[dayPref.day] = timeWindowIds
    }
  })
  return dayTimeMap
}

export const transformDayPreferencesToApi = (dayTimePreferences: Record<string, string[]>): DayPreferenceInput[] => {
  return Object.entries(dayTimePreferences)
    .filter(([_, timeWindows]) => timeWindows.length > 0)
    .map(([day, timeWindowIds]) => ({
      day,
      timeRanges: timeWindowIds.map((windowId) => {
        const window = TIME_WINDOWS.find((w) => w.id === windowId)
        return {
          start: window?.startTime || '',
          end: window?.endTime || '',
        }
      }),
    }))
}

export const getAddressString = (venueData: TheaterPreferencesVenue | undefined, fallbackLocation: string): string => {
  if (!venueData) return fallbackLocation || 'Selected location'
  return venueData.address
    ? `${venueData.address.line || ''}, ${venueData.address.city || ''}, ${venueData.address.zipCode || ''}`
    : venueData.addressText || fallbackLocation || 'Selected location'
}

// Helper function to format distance
export const formatDistance = (distance: number | undefined): string => {
  if (distance === undefined) return ''

  // Check if the distance is unreasonably large (likely in meters instead of miles)
  // A typical conversion factor from meters to miles is 0.000621371
  if (distance > 1000) {
    // Convert from meters to miles
    const milesDistance = distance * 0.000621371

    // For distances less than 10 miles, show one decimal place
    if (milesDistance < 10) {
      return `${milesDistance.toFixed(1)} mi`
    }

    // For larger distances, round to nearest mile
    return `${Math.round(milesDistance)} mi`
  }

  // If distance is less than 1 mile, show in feet (1 mile = 5280 feet)
  if (distance < 1) {
    const feet = Math.round(distance * 5280)
    return `${feet} ft`
  }

  // For distances less than 10 miles, show one decimal place
  if (distance < 10) {
    return `${distance.toFixed(1)} mi`
  }

  // For larger distances, round to nearest mile
  return `${Math.round(distance)} mi`
}

/**
 * Validates dayPreferences object structure and content
 * @param dayPreferences - The day preferences to validate (Record<string, string[]>)
 * @returns boolean - true if valid structure with values, false otherwise
 */
export const validateDayPreferences = (dayPreferences: unknown): dayPreferences is Record<string, string[]> => {
  if (!dayPreferences || typeof dayPreferences !== 'object' || Array.isArray(dayPreferences)) {
    return false
  }

  const prefs = dayPreferences
  const entries = Object.entries(prefs)

  if (entries.length === 0) {
    return false
  }

  const validDayIds = DAYS_OF_WEEK.map((day) => day.id)
  const validTimeWindowIds = TIME_WINDOWS.map((window) => window.id)

  return entries.every(([dayId, timeWindows]) => {
    if (!validDayIds.includes(dayId)) {
      return false
    }

    if (!Array.isArray(timeWindows)) {
      return false
    }

    return (
      timeWindows.length > 0 &&
      timeWindows.every((windowId) => typeof windowId === 'string' && validTimeWindowIds.includes(windowId))
    )
  })
}
